import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  BTAttendanceRecord,
  BTAttendanceUpdatePayload,
  BTClub,
  BTLocation,
} from '../../services/api/btApi';
import {
  BTAttendanceEditFieldErrors,
  BTAttendanceEditFormData,
  buildBTAttendanceUpdatePayload,
  createBTAttendanceEditForm,
  getAttendanceLocationLabel,
  validateBTAttendanceEditForm,
} from './btAttendanceEdit';

interface BTAttendanceEditDialogProps {
  record: BTAttendanceRecord | null;
  locations: BTLocation[];
  clubs: BTClub[];
  fallbackLocationId?: string;
  isLeadership: boolean;
  onClose: () => void;
  onSave: (payload: BTAttendanceUpdatePayload) => Promise<void>;
}

const EMPTY_FORM: BTAttendanceEditFormData = {
  name: '',
  phone: '',
  churchName: '',
  locationId: '',
  clubId: '',
  educationType: 'new',
};

const getClubLabel = (club: BTClub) => club.bt_club_name || club.club_name || club._id;

const BTAttendanceEditDialog: React.FC<BTAttendanceEditDialogProps> = ({
  record,
  locations,
  clubs,
  fallbackLocationId = '',
  isLeadership,
  onClose,
  onSave,
}) => {
  const preparedForm = record
    ? createBTAttendanceEditForm(record, locations, clubs, fallbackLocationId)
    : EMPTY_FORM;
  const [initialForm, setInitialForm] = useState<BTAttendanceEditFormData>(preparedForm);
  const [form, setForm] = useState<BTAttendanceEditFormData>(preparedForm);
  const [fieldErrors, setFieldErrors] = useState<BTAttendanceEditFieldErrors>({});
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!record) return;
    const resolvedForm = createBTAttendanceEditForm(record, locations, clubs, fallbackLocationId);

    setInitialForm((previous) => ({
      ...previous,
      locationId: previous.locationId || resolvedForm.locationId,
      clubId: previous.clubId || resolvedForm.clubId,
    }));
    setForm((previous) => ({
      ...previous,
      locationId: previous.locationId || resolvedForm.locationId,
      clubId: previous.clubId || resolvedForm.clubId,
    }));
    setFieldErrors((previous) => {
      const nextErrors = { ...previous };
      if (resolvedForm.locationId) delete nextErrors.locationId;
      if (resolvedForm.clubId) delete nextErrors.clubId;
      return nextErrors;
    });
  }, [record, locations, clubs, fallbackLocationId]);

  if (!record) return null;

  const updateField = <K extends keyof BTAttendanceEditFormData>(
    field: K,
    value: BTAttendanceEditFormData[K],
  ) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setFieldErrors((previous) => {
      const nextErrors = { ...previous };
      delete nextErrors[field];
      return nextErrors;
    });
    setSubmitError('');
  };

  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    const nextErrors = validateBTAttendanceEditForm(form, isLeadership);
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      const firstErrorField = Object.keys(nextErrors)[0] as keyof BTAttendanceEditFormData;
      document.getElementById(`bt-attendance-edit-${firstErrorField}`)?.focus();
      return;
    }

    const payload = buildBTAttendanceUpdatePayload(initialForm, form, isLeadership);
    if (Object.keys(payload).length === 0) {
      setSubmitError('변경된 내용이 없습니다.');
      return;
    }

    setSaving(true);
    setSubmitError('');
    try {
      await onSave(payload);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : '출석 기록 수정에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const teacherName = form.name.trim() || '선택한 교사';
  const checkInDate = record.checkInTime || record.createdAt;
  const hasUnknownLocation = Boolean(
    form.locationId && !locations.some((location) => location._id === form.locationId),
  );
  const hasUnknownClub = Boolean(form.clubId && !clubs.some((club) => club._id === form.clubId));

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      aria-labelledby="bt-attendance-edit-dialog-title"
      onClose={saving ? undefined : onClose}
    >
      <DialogTitle id="bt-attendance-edit-dialog-title">출석 기록 수정</DialogTitle>
      <Box component="form" noValidate onSubmit={handleSubmit}>
        <DialogContent dividers aria-busy={saving}>
          <Stack spacing={2.5}>
          <Alert severity="info">
            이 수정은 <strong>{teacherName}</strong>님의 현재 출석 기록 한 건에만 적용됩니다.
          </Alert>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
              p: 1.5,
              bgcolor: 'grey.50',
              borderRadius: 1,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">출석 유형</Typography>
              <Typography variant="body2">{isLeadership ? '리더십 학교' : 'Basic Training'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">강의</Typography>
              <Typography variant="body2">{record.session?.title || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">현재 장소</Typography>
              <Typography variant="body2">{getAttendanceLocationLabel(record)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">등록일시</Typography>
              <Typography variant="body2">
                {checkInDate ? new Date(checkInDate).toLocaleString('ko-KR') : '-'}
              </Typography>
            </Box>
          </Box>

          {submitError && <Alert severity="error" role="alert">{submitError}</Alert>}

          <TextField
            id="bt-attendance-edit-name"
            required
            fullWidth
            label="교사명"
            value={form.name}
            onChange={(event) => updateField('name', event.target.value)}
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name}
            disabled={saving}
          />
          <TextField
            id="bt-attendance-edit-phone"
            required
            fullWidth
            label="연락처"
            value={form.phone}
            onChange={(event) => updateField('phone', event.target.value)}
            error={Boolean(fieldErrors.phone)}
            helperText={fieldErrors.phone || '숫자 10~11자리'}
            disabled={saving}
            inputProps={{ inputMode: 'tel' }}
          />
          <TextField
            id="bt-attendance-edit-churchName"
            required
            fullWidth
            label="교회명"
            value={form.churchName}
            onChange={(event) => updateField('churchName', event.target.value)}
            error={Boolean(fieldErrors.churchName)}
            helperText={fieldErrors.churchName}
            disabled={saving}
          />

          {!isLeadership && (
            <>
              <FormControl required fullWidth error={Boolean(fieldErrors.locationId)} disabled={saving}>
                <InputLabel id="bt-attendance-edit-location-label">수강지역</InputLabel>
                <Select
                  id="bt-attendance-edit-locationId"
                  labelId="bt-attendance-edit-location-label"
                  label="수강지역"
                  value={form.locationId}
                  onChange={(event) => updateField('locationId', event.target.value)}
                >
                  {hasUnknownLocation && (
                    <MenuItem value={form.locationId} disabled>
                      {getAttendanceLocationLabel(record)} (기존 값)
                    </MenuItem>
                  )}
                  {locations.map((location) => (
                    <MenuItem key={location._id} value={location._id} disabled={location.isDeleted}>
                      {location.location_name}{location.isDeleted ? ' (삭제됨)' : ''}
                    </MenuItem>
                  ))}
                </Select>
                {fieldErrors.locationId && <FormHelperText>{fieldErrors.locationId}</FormHelperText>}
              </FormControl>

              <FormControl fullWidth disabled={saving}>
                <InputLabel id="bt-attendance-edit-club-label">클럽 (선택)</InputLabel>
                <Select
                  id="bt-attendance-edit-clubId"
                  labelId="bt-attendance-edit-club-label"
                  label="클럽 (선택)"
                  value={form.clubId}
                  onChange={(event) => updateField('clubId', event.target.value)}
                >
                  {hasUnknownClub && (
                    <MenuItem value={form.clubId} disabled>{record.bt_club_name || '기존 클럽'}</MenuItem>
                  )}
                  {clubs.map((club) => (
                    <MenuItem key={club._id} value={club._id}>{getClubLabel(club)}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>선택하지 않아도 저장할 수 있습니다.</FormHelperText>
              </FormControl>

              <FormControl required fullWidth error={Boolean(fieldErrors.educationType)} disabled={saving}>
                <InputLabel id="bt-attendance-edit-education-label">교육형태</InputLabel>
                <Select
                  id="bt-attendance-edit-educationType"
                  labelId="bt-attendance-edit-education-label"
                  label="교육형태"
                  value={form.educationType}
                  onChange={(event) => updateField(
                    'educationType',
                    event.target.value as BTAttendanceEditFormData['educationType'],
                  )}
                >
                  <MenuItem value="new">신규</MenuItem>
                  <MenuItem value="renewal">재교육</MenuItem>
                  <MenuItem value="advanced">심화</MenuItem>
                </Select>
                {fieldErrors.educationType && <FormHelperText>{fieldErrors.educationType}</FormHelperText>}
              </FormControl>
            </>
          )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button type="button" onClick={onClose} disabled={saving} sx={{ minHeight: 44 }}>취소</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            sx={{ minHeight: 44, minWidth: 104 }}
          >
            {saving ? (
              <>
                <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                저장 중…
              </>
            ) : '저장'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default BTAttendanceEditDialog;
